import { Card, Label, MonoMeta } from "@/design/primitives";
import { SOURCE_WORD } from "@/lib/waitingOnJeff";
import KitCapacity from "@/screens/cc/KitCapacity";
import { cn } from "@/lib/utils";

// ── MomentumRail (Jeff 10/26: "gamify it, make it look interesting") ────────
// The right rail of the Needs Me queue: three BIG numbers and a score line.
//   • Waiting  — the queue count, hero-sized (amber when it has weight,
//                green when zero — "all clear" should FEEL like all clear)
//   • Finished — state-flips to finished in the last 24h (the live events
//                feed; work does move without Jeff's word — celebrate it)
//   • Watching — healthy monitors standing guard so he doesn't have to
// Plus a per-source mini-read of what the queue is made of, and Kit's
// capacity in compact mode (the machine's own headroom, right where he
// decides). Every number derives from its own board/queue — absent data
// reads "—", never a fabricated 0.

const BIG = "font-mono text-[2rem] font-semibold leading-none tracking-[-0.02em] tabular-nums";

const HeroStat = ({ testid, n, label, tone }) => (
  <div data-testid={testid} className="flex flex-col gap-1">
    <span
      className={cn(
        BIG,
        tone === "green" ? "text-os-green" : tone === "amber" ? "text-os-amber" : "text-os-ink",
      )}
    >
      {n}
    </span>
    <MonoMeta as="span">{label}</MonoMeta>
  </div>
);

export default function MomentumRail({ count, events, bySource, healthyMonitors, live }) {
  // The last 24 hours of real motion: finished + fixed events, newest feed.
  const done24 = (events || []).filter(
    (e) => e.kind === "finished" || e.kind === "fixed",
  ).length;
  const failed24 = (events || []).filter((e) => e.kind === "failed").length;
  const sources = Object.entries(bySource || {}).filter(([, n]) => n > 0);

  return (
    <div data-testid="needsme-momentum" className="flex flex-col gap-4">
      <Card as="section" className="px-5 py-4">
        <Label as="h2">The desk right now</Label>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <HeroStat
            testid="needsme-stat-waiting"
            n={count === null || count === undefined ? "—" : count}
            label="waiting on your word"
            tone={live ? (count > 0 ? "amber" : "green") : undefined}
          />
          <HeroStat
            testid="needsme-stat-finished"
            n={live ? done24 : "—"}
            label="done / fixed · 24h"
            tone={done24 > 0 ? "green" : undefined}
          />
          <HeroStat
            testid="needsme-stat-watching"
            n={healthyMonitors === null || healthyMonitors === undefined ? "—" : healthyMonitors}
            label="watchers standing guard"
            tone={healthyMonitors > 0 ? "green" : undefined}
          />
        </div>
        {/* The score: what moved, what broke, since yesterday. Zero motion is
            fine to say — the feed only carries real flips. */}
        <p
          data-testid="needsme-momentum-score"
          className="mt-3 border-t border-os-border pt-2.5 text-[0.8125rem] leading-[1.45] text-os-ink-2"
        >
          {live ? (
            <>
              Last 24h: <span className="font-medium text-os-ink">{done24}</span> finished
              {failed24 > 0 ? (
                <>
                  {" · "}
                  <span className="font-medium text-os-amber">{failed24}</span> hit a wall
                </>
              ) : (
                " · nothing broke"
              )}
              {done24 === 0 && failed24 === 0 ? " — a quiet stretch, all fuel for the next push" : ""}
            </>
          ) : (
            "The boards are reconnecting — your numbers fill the moment they're back."
          )}
        </p>
        {sources.length > 0 ? (
          <div
            data-testid="needsme-momentum-sources"
            className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1"
          >
            {sources.map(([s, n]) => (
              <span key={s} className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">
                {n} {SOURCE_WORD[s] || s}
              </span>
            ))}
          </div>
        ) : null}
      </Card>

      {/* Kit's own headroom — the machine behind every "Kit is on it". */}
      <KitCapacity compact />
    </div>
  );
}